import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@renderer/chat/providers'
import { submitFeedback, type FeedbackRating, type FeedbackSubject } from '@renderer/chat/api/feedback'
import { useMessagesStore } from '@renderer/chat/store'

export function useMessageFeedback(subject: FeedbackSubject, currentRating: FeedbackRating | null = null) {
  const { getAuthToken } = useAuth()
  const { patchMessage } = useMessagesStore()
  const queryClient = useQueryClient()
  const feedbackQueryKey = subject.messageId
    ? (['feedback', 'message', subject.messageId] as const)
    : subject.stagedActionId
      ? (['feedback', 'staged-action', subject.stagedActionId] as const)
      : (['feedback', 'conversation', subject.conversationId] as const)
  // Observe optimistic writes without starting another read. Undefined follows
  // host hydration; null is a real rollback to unrated.
  const { data: cachedRating } = useQuery<FeedbackRating | null>({ queryKey: feedbackQueryKey, queryFn: skipToken })
  const rating = cachedRating === undefined ? currentRating : cachedRating
  const patchStoredMessage = (given_feedback: FeedbackRating | null) => {
    if (subject.messageId && subject.conversationId) {
      patchMessage(subject.conversationId, subject.messageId, { given_feedback })
    }
  }

  const applyRating = (nextRating: FeedbackRating | null) => {
    patchStoredMessage(nextRating)
    queryClient.setQueryData(feedbackQueryKey, nextRating)
  }

  const mutation = useMutation({
    // One subject can be mounted in several surfaces. Run its writes in click
    // order so an earlier failure cannot roll back a newer optimistic rating.
    scope: { id: feedbackQueryKey.join(':') },
    mutationFn: async ({ type, message }: { type: FeedbackRating; message: string }) => {
      if (!subject.messageId) await queryClient.cancelQueries({ queryKey: feedbackQueryKey })
      const cachedBeforeMutation = queryClient.getQueryData<FeedbackRating | null>(feedbackQueryKey)
      const previousRating = cachedBeforeMutation === undefined ? currentRating : cachedBeforeMutation
      applyRating(type)

      try {
        return await submitFeedback(subject, await getAuthToken(), { rating: type, message })
      } catch (error) {
        applyRating(previousRating)
        throw error
      }
    },
  })

  return { ...mutation, rating }
}

export type MessageFeedbackState = ReturnType<typeof useMessageFeedback>
